import { ClaimNode, SeasonId } from '../types/seasonPass';
import { getSeasonNodes as getMusicSeasonNodes, getNodeById as getMusicNodeById, DEFAULT_SEASON as MUSIC_DEFAULT_SEASON } from './seasonPassData';

import realOrAiImg from '../../../shared/assets/images/games/Real Or AI Thumbnail.png';
import defaultGameImage from '../../../shared/assets/images/games/default-game.png';

// Minimal AI placeholder: empty season nodes (can be expanded later)
const AI_SEASONS: Record<SeasonId, ClaimNode[]> = {
  season1: [
    {
      id: 'start_node',
      seasonId: 'season1',
      position: 0,
      type: 'start',
      title: 'Welcome to AI Adventures!',
      rewards: [],
      requirements: { type: 'always_unlocked' }
    },
    {
      id: 'pre_level_1',
      seasonId: 'season1',
      position: 1,
      type: 'level_reward',
      title: 'AI Basics',
      rewards: [
        { id: 'reward_ai_lab', type: 'game', gameId: 'overture-music-lab', name: 'Overture Lab', image: '/src/shared/assets/images/games/Overture Music Lab Thumbnail.png', rarity: 'common', description: 'Try creating with simple tools' }
      ],
      requirements: { type: 'node_claimed', nodeId: 'start_node' }
    },
    {
      id: 'post_level_1',
      seasonId: 'season1',
      position: 2,
      type: 'level_reward',
      levelId: '1',
      title: 'Pattern Finder',
      rewards: [],
      requirements: { type: 'level_complete', levelId: '1' }
    },
  ],
  season2: [],
};

// New curriculum: ai-2026-create-worlds - season1 nodes
const AI_2026_CREATE_WORLDS_SEASONS: Record<SeasonId, ClaimNode[]> = {
  season1: [
    {
      id: 'start_node',
      seasonId: 'season1',
      position: 0,
      type: 'start',
      title: 'Start',
      rewards: [],
      requirements: { type: 'always_unlocked' },
    },
    {
      id: 'pre_level_1',
      seasonId: 'season1',
      position: 1,
      type: 'level_reward',
      title: 'Getting Started',
      rewards: [
        {
          id: 'reward_real_or_ai',
          type: 'game',
          gameId: 'real-or-ai',
          name: 'Real or AI',
          image: realOrAiImg,
          rarity: 'common',
          description: 'Play Real or AI',
        },
        {
          id: 'reward_og_creators',
          type: 'game',
          gameId: 'og-creators',
          name: 'OG Create',
          image: defaultGameImage,
          rarity: 'common',
          description: 'Create prompts your teacher can review',
        },
      ],
      requirements: { type: 'node_claimed', nodeId: 'start_node' },
    },
  ],
  season2: [],
};

// New curriculum: ai-fall-2025 - season1 nodes
const AI_FALL_2025_SEASONS: Record<SeasonId, ClaimNode[]> = {
  season1: [
    {
      id: 'start_node',
      seasonId: 'season1',
      position: 0,
      type: 'start',
      title: 'Start',
      rewards: [],
      requirements: { type: 'always_unlocked' }
    },
    {
      id: 'pre_level_1',
      seasonId: 'season1',
      position: 1,
      type: 'level_reward',
      title: 'Getting Started',
      rewards: [
        { id: 'reward_real_or_ai', type: 'game', gameId: 'real-or-ai', name: 'Real or AI', image: '/src/shared/assets/images/games/Real Or AI Thumbnail.png', rarity: 'common', description: 'Play Real or AI' }
      ],
      requirements: { type: 'node_claimed', nodeId: 'start_node' }
    },
    // Post-level placeholders for 5 levels
    {
      id: 'post_level_1',
      seasonId: 'season1',
      position: 2,
      type: 'level_reward',
      levelId: '1',
      title: 'Level 1 Complete',
      rewards: [],
      requirements: { type: 'level_complete', levelId: '1' }
    },
    {
      id: 'post_level_2',
      seasonId: 'season1',
      position: 3,
      type: 'level_reward',
      levelId: '2',
      title: 'Level 2 Complete',
      rewards: [],
      requirements: { type: 'level_complete', levelId: '2' }
    },
    {
      id: 'post_level_3',
      seasonId: 'season1',
      position: 4,
      type: 'level_reward',
      levelId: '3',
      title: 'Level 3 Complete',
      rewards: [],
      requirements: { type: 'level_complete', levelId: '3' }
    },
    {
      id: 'post_level_4',
      seasonId: 'season1',
      position: 5,
      type: 'level_reward',
      levelId: '4',
      title: 'Level 4 Complete',
      rewards: [],
      requirements: { type: 'level_complete', levelId: '4' }
    },
    {
      id: 'post_level_5',
      seasonId: 'season1',
      position: 6,
      type: 'level_reward',
      levelId: '5',
      title: 'Level 5 Complete',
      rewards: [],
      requirements: { type: 'level_complete', levelId: '5' }
    },
  ],
  season2: [],
};

export function getSeasonNodesByCurriculum(curriculumId: string | null | undefined, seasonId: SeasonId) {
  const key = (curriculumId || 'music').trim().toLowerCase();
  if (key === 'ai') {
    return AI_SEASONS[seasonId] || [];
  }
  if (key === 'ai-2026-create-worlds') {
    return AI_2026_CREATE_WORLDS_SEASONS[seasonId] || [];
  }
  if (key === 'ai-fall-2025') {
    return AI_FALL_2025_SEASONS[seasonId] || [];
  }
  return getMusicSeasonNodes(seasonId);
}

export function getNodeByIdByCurriculum(curriculumId: string | null | undefined, nodeId: string, seasonId: SeasonId) {
  const key = (curriculumId || 'music').trim().toLowerCase();
  if (key === 'ai') {
    const nodes = getSeasonNodesByCurriculum(key, seasonId);
    return nodes.find(n => n.id === nodeId);
  }
  if (key === 'ai-2026-create-worlds') {
    const nodes = getSeasonNodesByCurriculum(key, seasonId);
    return nodes.find(n => n.id === nodeId);
  }
  if (key === 'ai-fall-2025') {
    const nodes = getSeasonNodesByCurriculum(key, seasonId);
    return nodes.find(n => n.id === nodeId);
  }
  return getMusicNodeById(nodeId, seasonId);
}

export function getDefaultSeasonForCurriculum(curriculumId: string | null | undefined) {
  // For now, both use season1. Later, can map per-curriculum.
  return MUSIC_DEFAULT_SEASON;
}

export function isCurriculumSeasonEmpty(curriculumId: string | null | undefined, seasonId: SeasonId) {
  const key = (curriculumId || 'music').trim().toLowerCase();
  if (key === 'ai') {
    return (AI_SEASONS[seasonId] || []).length === 0;
  }
  if (key === 'ai-2026-create-worlds') {
    return (AI_2026_CREATE_WORLDS_SEASONS[seasonId] || []).length === 0;
  }
  if (key === 'ai-fall-2025') {
    return (AI_FALL_2025_SEASONS[seasonId] || []).length === 0;
  }
  return (getMusicSeasonNodes(seasonId) || []).length === 0;
}
